import { apiCall, apiClient, baseUrl } from 'shared/client/apiClient';
import { SendEventRequestBodyType } from 'shared/common/dtos/xapi/sendEvent';
import { isFreeContentEmbedSignal } from 'shared/utils/freeContentEmbed';

type SendEventOptions = {
  useBeacon?: boolean;
};

const FREE_CONTENT_EMBED_FLAG = 'hendrx.freeContentEmbed';

/**
 * A 720 free-content embed (slxapi / host=infinity) must NOT use `sendBeacon`: beacon bypasses the
 * `apiClient` middleware, so it omits the `X-Client-Referrer` header that carries the slxapi context —
 * without which the server can't route the event to the external kata LRS. Keyed off the embed SIGNAL,
 * never `businessDomain` (D-E); the server stays authoritative.
 *
 * The signal is **sticky for the tab session**: SPA navigation (and the content-session exchange) can
 * drop `slxapi`/`host` from the current URL, but a session that started as an embed stays an embed — so
 * the exit events fired on pagehide (after navigation) still take the non-beacon path and keep the
 * referrer. Once detected, we persist a flag in sessionStorage and read it back on later calls. Detection
 * uses the canonical signal (slxapi OR host=infinity) so it still fires once slxapi has been stripped.
 */
const isFreeContentEmbed = (): boolean => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (isFreeContentEmbedSignal({ slxapi: params.get('slxapi'), host: params.get('host') })) {
    try {
      window.sessionStorage.setItem(FREE_CONTENT_EMBED_FLAG, '1');
    } catch {
      /* sessionStorage unavailable (private mode) — fall back to the current-URL check only */
    }
    return true;
  }
  try {
    return window.sessionStorage.getItem(FREE_CONTENT_EMBED_FLAG) === '1';
  } catch {
    return false;
  }
};

/**
 * This POST is the one hop in the event's journey that nothing else guards: an event that never reaches
 * the API is lost outright, since the server's own retries (`postWithRetry`) only cover the hops beyond
 * it. Attempt count and cadence follow the house default in `getQueryClient` — 3 retries at 1s / 2s / 4s.
 */
const SEND_EVENT_MAX_ATTEMPTS = 4;
const SEND_EVENT_RETRY_BASE_DELAY_MS = 1000;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retry only what the API demonstrably did not take. The endpoint answers 200 whenever it has accepted an
 * event — a failure forwarding it onwards is caught server-side and retried there, never surfaced here —
 * so a non-2xx means nothing was sent on and re-sending cannot duplicate a statement. 4xx is the
 * exception: the payload or the caller is being rejected, and repeating it changes nothing. No status at
 * all means the request never completed (offline, DNS, connection refused), which is the case this whole
 * mechanism exists for.
 */
const isRetryable = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status !== 'number') return true;
  return status >= 500 || status === 408 || status === 429;
};

export async function sendEvent(args: SendEventRequestBodyType, options: SendEventOptions = {}) {
  // Prime the sticky embed flag on EVERY call, not just the beacon path: the session's non-exit events
  // fire while slxapi/host are still on the URL, so detecting here sets the flag before the exit event
  // (pagehide → useBeacon) runs — by which time slxapi has typically been stripped from the URL.
  const embed = isFreeContentEmbed();

  if (options.useBeacon && navigator.sendBeacon && !embed) {
    const url = `${baseUrl}/xapi-events/`;
    try {
      const body = JSON.stringify(args);
      const blob = new Blob([body], { type: 'application/json' });
      const queued = navigator.sendBeacon(url, blob);
      if (queued) {
        return null;
      }
      console.error('sendBeacon was not queued, falling back to fetch');
    } catch (e) {
      console.error('sendBeacon failed, falling back to fetch:', e);
    }
  }

  // A single attempt on the unload path: the page is being torn down, so a scheduled retry either never
  // runs or — if the page was only frozen into the bfcache — resumes minutes later, and the API stamps the
  // statement when it arrives. A stale exit event is worse than a missing one. The request itself is
  // `keepalive`, so the browser still delivers this attempt after the page is gone.
  const maxAttempts = options.useBeacon ? 1 : SEND_EVENT_MAX_ATTEMPTS;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await apiCall(apiClient.POST('/xapi-events/', { body: args, cache: 'no-cache', keepalive: true }));
    } catch (error) {
      if (attempt === maxAttempts || !isRetryable(error)) {
        console.error('Failed to send xAPI event:', error);
        return null;
      }
      console.warn(`sendEvent attempt ${attempt}/${maxAttempts} failed, retrying:`, error);
      await wait(SEND_EVENT_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
    }
  }

  return null;
}
